/**
 * SAP credit risk class codes (from the customer credit segment / BP master).
 *
 * Authored as name -> code so call sites read `RISK_CLASSES.low_risk` instead of the opaque `'Z04'`
 * magic string. The code is what gets persisted in the database and compared against; the name is
 * the developer-facing handle and doubles as the i18n key (translate it under the `riskClass`
 * block), so the UI shows "Low Risk" / "Risque faible" and no one ever authors or translates `Z04`.
 *
 * Note: `Z05` is intentionally absent - it is not used by SAP.
 */
export const RISK_CLASSES = {
  provisional: 'Z01',
  high_risk: 'Z02',
  medium_risk: 'Z03',
  low_risk: 'Z04',
  very_low_risk: 'Z06',
  block: 'Z07',
} as const

export type RiskClassName = keyof typeof RISK_CLASSES
export type RiskClassCode = (typeof RISK_CLASSES)[RiskClassName]

export const RISK_CLASS_NAMES = Object.keys(RISK_CLASSES) as RiskClassName[]
export const RISK_CLASS_CODES = Object.values(RISK_CLASSES) as RiskClassCode[]

export const RISK_CLASS_NAME_BY_CODE = Object.fromEntries(
  Object.entries(RISK_CLASSES).map(([name, code]) => [code, name]),
) as Record<RiskClassCode, RiskClassName>

export function isRiskClassCode(value: string | null | undefined): value is RiskClassCode {
  return value != null && RISK_CLASS_NAME_BY_CODE[value as RiskClassCode] !== undefined
}

export function getRiskClassName(code: string): RiskClassName | string {
  return RISK_CLASS_NAME_BY_CODE[code as RiskClassCode] ?? code
}

/**
 * Present and not one of the codes SAP is expected to send. Null or empty is "no credit segment /
 * no class", which is missing, not invalid. The BP extract stores the class as free text and the
 * import checks nothing, so this is where a garbage code becomes visible.
 */
export function hasInvalidRiskClass(value: string | null | undefined): boolean {
  return value != null && value !== '' && !isRiskClassCode(value)
}
